/**
 * TEXTO EN ARCO.
 *
 * La mitad de los encargos reales —escudos de club, peñas, despedidas— llevan
 * el nombre curvado sobre un logo. Sin esto, esos pedidos no se pueden hacer
 * aqui y acaban por WhatsApp.
 *
 * Geometria PURA en milimetros: ni Fabric ni canvas ni DOM. Asi se puede probar
 * de verdad y, el dia que el compositor de produccion tenga que dibujar el
 * mismo arco en el SVG final, usa esta misma funcion y sale identico. Eso es lo
 * que hace que lo que se ve en pantalla sea lo que se imprime.
 */

export interface CaracterEnArco {
  /** El caracter, tal cual. */
  caracter: string;
  /** Centro del caracter, en mm, relativo al centro del texto. */
  xMm: number;
  yMm: number;
  /** Giro del caracter en GRADOS, en el sentido de las agujas del reloj. */
  gradosGiro: number;
}

/** El angulo total que ocupa el texto sobre la circunferencia, en radianes. */
export function anguloTotal(anchoTotalMm: number, radioMm: number): number {
  if (radioMm <= 0) return 0;
  return anchoTotalMm / radioMm;
}

/**
 * Reparte los caracteres sobre un arco.
 *
 * `anchosMm` es el ancho REAL de cada caracter, medido con la tipografia ya
 * cargada. No se estima: una "i" y una "W" no ocupan lo mismo, y estimarlo
 * amontona unas letras y separa otras — es el defecto que delata al texto
 * curvado hecho a ojo.
 *
 * El resultado va centrado en el origen y con la referencia puesta arriba
 * (direccion 'up') o abajo ('down'), que es como se lee un escudo.
 */
export function disponerEnArco(
  caracteres: readonly string[],
  anchosMm: readonly number[],
  radioMm: number,
  direccion: 'up' | 'down',
): CaracterEnArco[] {
  if (caracteres.length === 0 || radioMm <= 0) return [];

  const anchoTotal = anchosMm.reduce((suma, ancho) => suma + ancho, 0);
  const signo = direccion === 'up' ? 1 : -1;

  // Se avanza por el arco acumulando anchos y se coloca cada caracter en su
  // punto MEDIO: colocarlo en el borde inclina mal las letras anchas.
  let recorrido = 0;
  const salida: CaracterEnArco[] = [];

  for (let i = 0; i < caracteres.length; i += 1) {
    const ancho = anchosMm[i] ?? 0;
    const centro = recorrido + ancho / 2;
    recorrido += ancho;

    // Angulo respecto al eje vertical, negativo a la izquierda.
    const angulo = (centro - anchoTotal / 2) / radioMm;

    salida.push({
      caracter: caracteres[i] as string,
      xMm: radioMm * Math.sin(angulo),
      /*
        Y crece HACIA ABAJO, como en el lienzo.

        En 'up' —el nombre en la parte de arriba de un escudo— el centro es el
        punto mas alto y los extremos caen: y positivo en los extremos, cero en
        el medio. En 'down' es el espejo.
      */
      yMm: signo * (radioMm - radioMm * Math.cos(angulo)),
      gradosGiro: ((angulo * 180) / Math.PI) * signo,
    });
  }

  return salida;
}

/**
 * Si el texto da mas de una vuelta completa, el radio es demasiado pequeno para
 * ese texto y las letras se pisan. Mejor decirlo que dibujar un churro.
 */
export function seSolapaEnArco(anchoTotalMm: number, radioMm: number): boolean {
  return anguloTotal(anchoTotalMm, radioMm) > 2 * Math.PI;
}

/**
 * Alto y ancho que ocupa el texto ya curvado, en mm.
 *
 * Hace falta para que la validacion de la zona juzgue el arco y no la caja del
 * texto recto: un texto de 80 mm curvado en un radio pequeno ocupa mucho menos
 * de ancho y mucho mas de alto.
 */
export function medidaDelArco(
  posiciones: readonly CaracterEnArco[],
  altoCaracterMm: number,
): { anchoMm: number; altoMm: number } {
  if (posiciones.length === 0) return { anchoMm: 0, altoMm: 0 };
  const xs = posiciones.map((p) => p.xMm);
  const ys = posiciones.map((p) => p.yMm);
  return {
    anchoMm: Math.max(...xs) - Math.min(...xs) + altoCaracterMm * 0.6,
    altoMm: Math.max(...ys) - Math.min(...ys) + altoCaracterMm,
  };
}
